/**
 * What entered the wormhole each day, and what left it.
 *
 * A **diverging bar chart with no second hue.** In and out are opposite
 * directions, which is the textbook case for a diverging palette — two hues
 * either side of a neutral midpoint. This site cannot have one: bronze and
 * crimson are adjacent hues that fail CVD separation as a categorical pair, so
 * every chart here is single-series (see `index.css` and `CLAUDE.md`).
 *
 * The resolution is that **direction is carried by position, not by colour**.
 * A bar above the zero rule arrived; one below it left. That is a stronger
 * encoding than hue anyway — it survives greyscale, print, forced-colors and
 * every form of colour vision — and it means the chart has no categorical pair
 * to validate. One hue, one axis, magnitude by length.
 *
 * One axis is load-bearing: both directions are the same measure in the same
 * unit, scaled symmetrically about zero. Two scales would invent a relationship
 * between arrivals and departures that is not in the data.
 *
 * Inline SVG for the same reason `ShareChart` is: the whole thing is a handful
 * of rects and two rules, and a charting library would be a second set of
 * theming rules to keep in step with the custom properties above it.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import type { DailyWormhole } from '../api/generated/DailyWormhole'
import { tokens, utcDay } from '../lib/format'

const HEIGHT = 260
const PAD = { top: 20, right: 16, bottom: 34, left: 62 }
/** Used until the container has been measured, and if ResizeObserver is absent. */
const FALLBACK_WIDTH = 720
/** The 2px surface gap the house style puts between adjacent fills. */
const BAR_GAP = 2
/** Rounded data-ends, per the mark spec. Clamped for bars shorter than this. */
const BAR_RADIUS = 4
/**
 * Widest a single day may be drawn.
 *
 * Without it a short window spreads five days across a full-width panel and
 * draws 170px slabs — the "thick saturated blocks" anti-pattern, and wide
 * enough that the zero rule disappears under them and the two directions read
 * as one mass. Marks stay thin; the columns space out instead.
 */
const MAX_BAR_WIDTH = 34
/**
 * Half the surface gap held open at the zero rule.
 *
 * Load-bearing, not decoration. The two directions wear the same hue, so a day
 * whose bars meet at zero draws as one continuous mark running from below the
 * rule to above it — the direction encoding disappears exactly where it has to
 * work. The house style already asks for a 2px surface gap between adjacent
 * fills; this is that gap, and it is what lets the rule be seen between them.
 */
const ZERO_GAP = 1.5

interface Placed {
  day: DailyWormhole
  /** Left edge of the day's column. */
  x: number
  w: number
  /** Value in, as a float of whole tokens. */
  inValue: number
  outValue: number
}

function useMeasuredWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(FALLBACK_WIDTH)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(320, entry.contentRect.width))
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  return [ref, width]
}

/**
 * A raw amount as a float of whole tokens.
 *
 * Only ever used to decide how tall to draw a rectangle. Every figure a reader
 * actually sees goes through `tokens`, which is exact — a chain amount is a
 * 128-bit integer and `Number` loses it above 2^53, so this is safe for
 * geometry and nothing else.
 */
function scaled(raw: string, decimals: number): number {
  const n = Number(raw)
  if (!Number.isFinite(n)) return 0
  return n / 10 ** decimals
}

/** A UTC day bucket, as its own date in every zone (blackbeard/observer#38). */
const dayLabel = (iso: string) => utcDay(iso)

/** An axis figure: thousands, rounded, no decimals — the tooltip carries exact. */
function axisValue(v: number): string {
  const abs = Math.abs(v)
  if (abs >= 1000) return `${Math.round(abs / 100) / 10}k`
  if (abs >= 10) return String(Math.round(abs))
  return String(Math.round(abs * 10) / 10)
}

export function FlowChart({
  days,
  decimals,
  symbol,
}: {
  days: DailyWormhole[]
  decimals: number
  symbol: string
}) {
  const [wrap, width] = useMeasuredWidth()
  const [hover, setHover] = useState<Placed | null>(null)

  const chart = useMemo(() => {
    // Days before anything was indexed are not zero-value days, they are days
    // with no data — drawing them as an empty run implies the pool was idle.
    const firstActive = days.findIndex((d) => d.entered !== '0' || d.left !== '0')
    const shown = firstActive === -1 ? [] : days.slice(firstActive)
    if (shown.length === 0) return null

    const plotW = width - PAD.left - PAD.right
    const plotH = HEIGHT - PAD.top - PAD.bottom
    // Symmetric about zero: the same length means the same amount whichever
    // way it points, which is the whole claim a diverging chart makes.
    const peak = Math.max(
      ...shown.map((d) => Math.max(scaled(d.entered, decimals), scaled(d.left, decimals))),
      1,
    )
    const top = peak * 1.1
    const zero = PAD.top + plotH / 2
    const half = plotH / 2

    const slot = plotW / shown.length
    const w = Math.min(MAX_BAR_WIDTH, Math.max(2, slot - BAR_GAP))
    const placed: Placed[] = shown.map((day, i) => ({
      day,
      // Centred in its slot rather than left-aligned: once the width is capped
      // the two stop being the same thing, and a left-aligned run of narrow
      // bars drifts away from its own axis labels.
      x: PAD.left + i * slot + (slot - w) / 2,
      w,
      inValue: scaled(day.entered, decimals),
      outValue: scaled(day.left, decimals),
    }))

    const h = (v: number) => (v / top) * half
    // Ends and middle only; more collide on a narrow panel and the tooltip
    // carries the exact day for any bar.
    const xTicks = [0, Math.floor((shown.length - 1) / 2), shown.length - 1]
      .filter((i, n, a) => a.indexOf(i) === n)
      .map((i) => ({ i, p: placed[i] }))
      .filter((t): t is { i: number; p: Placed } => t.p !== undefined)

    const peakIn = placed.reduce((a, b) => (b.inValue > a.inValue ? b : a), placed[0]!)
    const peakOut = placed.reduce((a, b) => (b.outValue > a.outValue ? b : a), placed[0]!)

    const totalIn = placed.reduce((s, p) => s + p.inValue, 0)
    const totalOut = placed.reduce((s, p) => s + p.outValue, 0)

    return { placed, zero, half, top, h, xTicks, peakIn, peakOut, totalIn, totalOut }
  }, [days, width, decimals])

  if (!chart) {
    return <p className="empty">Nothing has moved through the wormhole in this window yet.</p>
  }

  const onMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const local = event.clientX - rect.left
    let nearest: Placed | null = null
    let best = Infinity
    for (const p of chart.placed) {
      // Distance to the column's centre, so the hit target is the whole slot
      // rather than the drawn bar — a near-zero day is still reachable.
      const d = Math.abs(p.x + p.w / 2 - local)
      if (d < best) {
        best = d
        nearest = p
      }
    }
    setHover(nearest)
  }

  /**
   * One bar, as a path rather than a rect.
   *
   * The mark spec asks for rounded *data-ends* anchored to the baseline. An
   * SVG `rect` cannot do that — `rx` rounds all four corners, so the end that
   * meets the zero rule gets a radius too and each bar reads as a detached
   * lozenge with a notch where it should be anchored. A path rounds the two
   * corners at the far end and leaves the baseline square.
   *
   * A bar shorter than its own radius takes a smaller one rather than curling.
   */
  const barPath = (x: number, w: number, height: number, up: boolean): string => {
    const h = Math.max(height, 0)
    if (h <= 0) return ''
    const r = Math.min(BAR_RADIUS, w / 2, h)
    const s = up ? 1 : -1
    const y0 = chart.zero - s * ZERO_GAP
    const y1 = y0 - s * h
    return [
      `M${x} ${y0}`,
      `L${x} ${y1 + s * r}`,
      `Q${x} ${y1} ${x + r} ${y1}`,
      `L${x + w - r} ${y1}`,
      `Q${x + w} ${y1} ${x + w} ${y1 + s * r}`,
      `L${x + w} ${y0}`,
      'Z',
    ].join(' ')
  }

  const gridLines = [1, 0.5, -0.5, -1].map((f) => ({
    f,
    y: chart.zero - f * chart.half,
    label: axisValue(chart.top * Math.abs(f)),
  }))

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <svg
        className="chart"
        width={width}
        height={HEIGHT}
        viewBox={`0 0 ${width} ${HEIGHT}`}
        role="img"
        aria-label={`Value through the wormhole each day. Over this window ${tokens(
          String(Math.round(chart.totalIn)),
          0,
          0,
        )} ${symbol} arrived and ${tokens(String(Math.round(chart.totalOut)), 0, 0)} ${symbol} left. Bars above the centre rule arrived, bars below it left.`}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        {gridLines.map((g) => (
          <g key={g.f}>
            <line className="grid" x1={PAD.left} x2={width - PAD.right} y1={g.y} y2={g.y} />
            <text className="chart-axis" x={PAD.left - 8} y={g.y + 3.5} textAnchor="end">
              {g.label}
            </text>
          </g>
        ))}

        <text className="chart-axis" x={PAD.left - 8} y={chart.zero + 3.5} textAnchor="end">
          0
        </text>

        {/* Direction, said in words. The whole design rests on side-of-the-rule
            carrying what a second hue would, so it is labelled rather than
            left to be inferred. */}
        <text className="chart-axis" x={width - PAD.right} y={PAD.top - 7} textAnchor="end">
          in ↑
        </text>
        <text
          className="chart-axis"
          x={width - PAD.right}
          y={HEIGHT - PAD.bottom + 14}
          textAnchor="end"
        >
          out ↓
        </text>

        {chart.placed.map((p) => (
          <g key={p.day.day}>
            <path className="flow-in" d={barPath(p.x, p.w, chart.h(p.inValue), true)} />
            <path className="flow-out" d={barPath(p.x, p.w, chart.h(p.outValue), false)} />
          </g>
        ))}

        {/* The zero rule is drawn last so it sits *on* the bars. It is what
            makes a bar's side meaningful, and a spine hidden behind the marks
            it anchors is no spine at all. */}
        <line
          className="flow-zero"
          x1={PAD.left}
          x2={width - PAD.right}
          y1={chart.zero}
          y2={chart.zero}
        />

        {hover && (
          <rect
            className="flow-hover"
            x={hover.x - BAR_GAP / 2}
            y={PAD.top}
            width={hover.w + BAR_GAP}
            height={HEIGHT - PAD.top - PAD.bottom}
          />
        )}

        {chart.xTicks.map((t, i) => (
          <text
            key={t.i}
            className="chart-axis"
            x={t.p.x + t.p.w / 2}
            y={HEIGHT - 8}
            textAnchor={i === 0 ? 'start' : i === chart.xTicks.length - 1 ? 'end' : 'middle'}
          >
            {dayLabel(t.p.day.day)}
          </text>
        ))}

        {/* Two direct labels — the busiest day each way. Selective, per the
            mark spec: a number on every bar would go unread. */}
        {!hover && (
          <>
            <text
              className="chart-axis"
              x={chart.peakIn.x + chart.peakIn.w / 2}
              y={Math.max(chart.zero - chart.h(chart.peakIn.inValue) - 6, PAD.top + 9)}
              textAnchor="middle"
              style={{ fill: 'var(--data-bright)', fontSize: 11 }}
            >
              {axisValue(chart.peakIn.inValue)}
            </text>
            <text
              className="chart-axis"
              x={chart.peakOut.x + chart.peakOut.w / 2}
              y={Math.min(
                chart.zero + chart.h(chart.peakOut.outValue) + 13,
                HEIGHT - PAD.bottom - 2,
              )}
              textAnchor="middle"
              style={{ fill: 'var(--data-bright)', fontSize: 11 }}
            >
              {axisValue(chart.peakOut.outValue)}
            </text>
          </>
        )}
      </svg>

      {hover && (
        <div
          className="tooltip"
          style={{
            left: hover.x,
            top: 4,
            transform: hover.x > width / 2 ? 'translate(-100%, 0)' : 'translate(10px, 0)',
          }}
        >
          <div className="tooltip-label">{dayLabel(hover.day.day)}</div>
          <div>
            <strong>{tokens(hover.day.entered, decimals, 1)}</strong> {symbol} in
          </div>
          <div>
            <strong>{tokens(hover.day.left, decimals, 1)}</strong> {symbol} out
            {hover.day.exits > 0 && `, over ${hover.day.exits.toLocaleString('en-US')} exits`}
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>
            {hover.day.notes.toLocaleString('en-US')} notes created
          </div>
        </div>
      )}

      {/* The table view the chart's numbers would otherwise only exist in.
          Visually hidden rather than absent: a screen reader, a text browser and
          a copy-paste all reach the series this way, and the summary in
          `aria-label` is a summary rather than the data. */}
      <table className="visually-hidden">
        <caption>Value through the wormhole each day</caption>
        <thead>
          <tr>
            <th scope="col">Day (UTC)</th>
            <th scope="col">In ({symbol})</th>
            <th scope="col">Out ({symbol})</th>
            <th scope="col">Exits</th>
          </tr>
        </thead>
        <tbody>
          {chart.placed.map((p) => (
            <tr key={p.day.day}>
              <th scope="row">{dayLabel(p.day.day)}</th>
              <td>{tokens(p.day.entered, decimals, 1)}</td>
              <td>{tokens(p.day.left, decimals, 1)}</td>
              <td>{p.day.exits}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
